import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { DeleteConfirmationModal } from '../../core/components/DeleteConfirmationModal'
import { MessageBox } from '../../core/components/MessageBox'
import {IAgeCategory, INameId} from "../../core/interfaces";
import {UUID} from "node:crypto";
import {Pagination, SimpleSearchPaginationParams} from "../../core/services/utils";
import axios, {AxiosResponse} from 'axios';

export interface AgeCategoryModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  setEditMode: (editMode: boolean) => void
  editMode: boolean
  page: number
  size: number
  setTotalElements: (elements: number) => void
  setAllList: (categories: IAgeCategory[]) => void
  ageCategoryEntity?: IAgeCategory
  createCall: (data: IAgeCategory) => Promise<AxiosResponse<IAgeCategory>>
  deleteCall: (id: UUID) => Promise<AxiosResponse<any>>
  updateCall: (id: UUID, data: IAgeCategory) =>  Promise<AxiosResponse<IAgeCategory>>
  getAllCall: (params?: SimpleSearchPaginationParams) =>  Promise<AxiosResponse<Pagination<IAgeCategory>>>
}
export interface AgeCategoryModeledId {
  name?: string
  startAge?: number
  endAge?: number
}

export const AgeCategoryModal = (
  props: AgeCategoryModalProps,
): JSX.Element => {
  const {
    ageCategoryEntity,
    setShowCreateModal,
    setAllList,
    showCreateModal,
    setEditMode,
    editMode,
    page,
    size,
    setTotalElements,
    createCall,
    deleteCall,
    updateCall,
    getAllCall

  } = props

  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    if (editMode) {
      setValue('name', ageCategoryEntity?.name)
      setValue('startAge', ageCategoryEntity?.startAge)
      setValue('endAge', ageCategoryEntity?.endAge)
    }
  }, [ageCategoryEntity, editMode])

  const schema = yup.object().shape({
    name: yup.string(),
    startAge: yup.number(),
    endAge: yup.number(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
  } = useForm<AgeCategoryModeledId>({
    mode: 'onChange',
    resolver: yupResolver<AgeCategoryModeledId>(schema),
    defaultValues: {
      name: '',
      startAge: 0,
      endAge: 0
    },
  })

  const handleDelete = () => {
    // prettier-ignore
    (async () => {
      try {
        if (ageCategoryEntity) {
          const { status } = await deleteCall(ageCategoryEntity?.id)
          const response = await getAllCall({
            page: page - 1,
            size,
          })
          setTotalElements(response.data.totalElements)
          setAllList(response.data.content)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  const handleClose = () => {
    setShowCreateModal(false)
    setEditMode(false)
    reset()
  }

  const submit = (values: AgeCategoryModeledId) => {
    // prettier-ignore
    (async () => {
      try {
        const data: IAgeCategory = {
          name: values.name,
          startAge: values.startAge,
          endAge: values.endAge
        }
        if (ageCategoryEntity) {
          const { status } = await updateCall(ageCategoryEntity.id, data)
          const response = await getAllCall({
            page: page - 1,
            size,
          })
          setAllList(response.data.content)
          if (status === 200 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Update Successful')
          }
        } else {
          const { status } = await createCall(data)
          const response = await getAllCall({
            page: page - 1,
            size,
          })
          setTotalElements(response.data.totalElements)
          setAllList(response.data.content)
          if (status === 200 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Create Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  return (
    <>
      <Modal
        show={showCreateModal}
        onHide={handleClose}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop={"static"}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode
              ? `Edit ${ageCategoryEntity?.name}`
              : `Add New`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="age-category-form" onSubmit={handleSubmit(submit)}>
            <Form.Group className="d-flex flex-column align-items-start gap-3">
              <Form.Group className="d-flex align-items-center gap-3 align-self-stretch">
              <Form.Label
                  style={{
                    fontSize: '16px',
                    fontFamily: 'Helvetica Neue',
                    fontWeight: '700',
                    lineHeight: '150%',
                    fontStyle: 'normal',
                    width: '132px',
                  }}
                  className="d-flex flex-column mb-0"
              >
                Name
              </Form.Label>
              <Form.Control
                  isInvalid={!!errors?.name?.message}
                  {...register('name')}
                  type="text"
                  placeholder="Enter Name"
              />
            </Form.Group>

              <Form.Group className="d-flex align-items-center gap-3 align-self-stretch">
                <Form.Label
                    style={{
                      fontSize: '16px',
                      fontFamily: 'Helvetica Neue',
                      fontWeight: '700',
                      lineHeight: '150%',
                      fontStyle: 'normal',
                      width: '132px',
                    }}
                    className="d-flex flex-column mb-0"
                >
                  Start Age
                </Form.Label>
                <Form.Control
                    isInvalid={!!errors?.startAge?.message}
                    {...register('startAge')}
                    type="number"
                    placeholder="Enter Start Age"
                />

                <Form.Label
                    style={{
                      fontSize: '16px',
                      fontFamily: 'Helvetica Neue',
                      fontWeight: '700',
                      lineHeight: '150%',
                      fontStyle: 'normal',
                      width: '132px',
                    }}
                    className="d-flex flex-column mb-0"
                >
                  End Age
                </Form.Label>
                <Form.Control
                    isInvalid={!!errors?.endAge?.message}
                    {...register('endAge')}
                    type="number"
                    placeholder="Enter End Age"
                />
              </Form.Group>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {editMode && (
            <Button
              onClick={handleDelete}
              style={{ color: '#FFFFFF' }}
              className="btn btn-danger me-auto "
            >
              Delete
            </Button>
          )}
          <Button
            onClick={handleClose}
            className="btn btn-secondary"
            data-dismiss="modal"
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            form="age-category-form"
            type="submit"
            disabled={Object.keys(dirtyFields).length === 0}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Delete'}
        deleteFunc={handleDelete}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
